<script lang="ts" setup>
  import RightNavContent from '@/components/landingPage/nav/RightNavContent.vue'
  import GlobalSearchTrigger from '@/components/misc/GlobalSearchTrigger.vue'
  import siteLogo from '@/assets/logo/logo_long_light_baked.svg'
  import type { JwtContent } from '@/types'
  import { ref, computed, onMounted, onUnmounted } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { docsUrl } from '@/utils/docsUrl'

  const { t, locale } = useI18n()
  // Plain anchor, not router-link: see the note in @/utils/docsUrl.
  const docsHref = computed(() => docsUrl(locale.value))

  const props = defineProps<{
    cartItems: number
    jwt: JwtContent
    loggedIn: boolean
  }>()

  const emits = defineEmits<{
    (e: 'logout', logout: boolean): void
  }>()

  const handleLogout = (e: boolean) => {
    emits('logout', e)
  }

  const productMenu = ref(false)

  const products = computed(() => [
    { name: 'kubernetes', icon: 'mdi-kubernetes', label: 'Kubernetes' },
    { name: 'vserver', icon: 'mdi-server-network', label: 'VServer' },
    { name: 'webhosting', icon: 'mdi-server', label: 'Webhosting' },
    { name: 'domainsSearch', icon: 'mdi-web', label: 'Domains' },
    { name: 'objectStorage', icon: 'mdi-database', label: 'Object Storage' },
    { name: 'emailHosting', icon: 'mdi-email-outline', label: 'E-Mail Hosting' },
    { name: 'pricingCalculator', icon: 'mdi-calculator-variant-outline', label: t('pricingCalculator.title') }
  ])

  // Scroll-aware nav state
  const isScrolled = ref(false)

  const handleScroll = () => {
    isScrolled.value = window.scrollY > 20
  }

  onMounted(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
  })

  onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll)
  })
</script>

<template>
  <!-- 700px and up -->
  <v-app-bar
    :aria-label="t('a11y.mainNavigation')"
    class="nav-app-bar"
    :class="{ scrolled: isScrolled }"
    color="transparent"
    elevation="0"
    flat
    role="navigation"
  >
    <v-container fluid>
      <!-- Main-nav -->
      <v-sheet class="header-grid" color="transparent">
        <!-- Left -->
        <v-sheet color="transparent">
          <router-link :to="{ name: 'landingPage' }" class="logo-link">
            <div class="logo-glow"></div>
            <img :src="siteLogo" :alt="t('a11y.logoToHome')" class="logo-img" />
          </router-link>
        </v-sheet>

        <!-- Center (exact center) - Product nav for non-logged in, dashboard CTA for logged in -->
        <v-sheet class="header-center" color="transparent">
          <v-menu
            v-if="!props.loggedIn"
            v-model="productMenu"
            :close-on-content-click="true"
            location="bottom center"
            offset="8"
          >
            <template #activator="{ props: menuProps }">
              <button v-bind="menuProps" class="nav-trigger" :class="{ active: productMenu }">
                <v-icon icon="mdi-view-grid-outline" size="18" />
                <span>{{ $t('landing.nav.products') }}</span>
                <v-icon
                  :icon="productMenu ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                  size="16"
                  class="chevron"
                />
              </button>
            </template>

            <v-card class="product-menu" rounded="lg" elevation="0">
              <router-link
                v-for="product in products"
                :key="product.name"
                :to="{ name: product.name }"
                class="product-menu-item"
              >
                <v-icon :icon="product.icon" size="20" />
                <span>{{ product.label }}</span>
              </router-link>
              <v-divider class="my-1" />
              <a :href="docsHref" class="product-menu-item">
                <v-icon icon="mdi-book-open-variant" size="20" />
                <span>{{ $t('landing.footer.docs') }}</span>
              </a>
              <router-link :to="{ name: 'blogIndex' }" class="product-menu-item">
                <v-icon icon="mdi-post-outline" size="20" />
                <span>{{ $t('blog.heading') }}</span>
              </router-link>
            </v-card>
          </v-menu>
          <!-- Show dashboard CTA for logged in users -->
          <router-link v-else :to="{ name: 'dashboardOverview' }" class="dashboard-cta">
            <v-icon icon="mdi-view-dashboard-outline" size="20" />
            <span>{{ $t('breadcrumbs.dashboard') }}</span>
          </router-link>
        </v-sheet>

        <!-- Right -->
        <v-sheet class="header-right d-flex align-center" color="transparent">
          <GlobalSearchTrigger />
          <right-nav-content
            :cart-items="props.cartItems"
            :jwt="props.jwt"
            :logged-in="props.loggedIn"
            @logout="handleLogout"
          />
        </v-sheet>
      </v-sheet>
    </v-container>
  </v-app-bar>
</template>

<style lang="scss" scoped>
  .logo-img {
    height: 30px;
    width: auto;
    display: block;
    position: relative;
    z-index: 1;
  }

  .nav-app-bar {
    background: rgba(10, 12, 16, 0.4);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border-bottom: 1px solid rgba(120, 163, 255, 0.08);
    transition: all 0.3s ease;

    &.scrolled {
      background: rgba(10, 12, 16, 0.85);
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      border-bottom-color: rgba(120, 163, 255, 0.15);
      box-shadow: 0 4px 30px rgba(0, 0, 0, 0.3);
    }
  }

  .header-grid {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    width: 100%;
  }

  /* Logo with glow effect */
  .logo-link {
    position: relative;
    display: flex;
    align-items: center;
    text-decoration: none;
    padding: 8px 12px;
    border-radius: 12px;
    transition: all 0.2s ease;
    width: fit-content;

    &:hover {
      background: rgba(255, 255, 255, 0.03);

      .logo-glow {
        opacity: 1;
      }
    }
  }

  .logo-glow {
    position: absolute;
    inset: -4px;
    background: radial-gradient(ellipse at center, rgba(0, 176, 255, 0.15), transparent 70%);
    border-radius: 16px;
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
    z-index: 0;
  }

  /* Center stays centered regardless of side widths */
  .header-center {
    justify-self: center;
    display: flex;
    align-items: center;
  }

  /* Products dropdown trigger */
  .nav-trigger {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(120, 163, 255, 0.1);
    border-radius: 12px;
    color: rgba(200, 218, 255, 0.7);
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    .v-icon {
      opacity: 0.6;
      transition: all 0.2s ease;
    }

    .chevron {
      opacity: 0.4;
      margin-left: -2px;
    }

    &:hover,
    &.active {
      background: rgba(255, 255, 255, 0.06);
      color: rgba(255, 255, 255, 0.95);
      border-color: rgba(120, 163, 255, 0.2);

      .v-icon {
        opacity: 0.9;
      }
    }
  }

  /* Dashboard CTA for logged in users */
  .dashboard-cta {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 24px;
    background: linear-gradient(135deg, rgba(0, 176, 255, 0.15), rgba(31, 226, 212, 0.1));
    border: 1px solid rgba(0, 176, 255, 0.3);
    border-radius: 12px;
    color: #00B0FF;
    font-size: 0.95rem;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.2s ease;

    .v-icon {
      transition: transform 0.2s ease;
    }

    &:hover {
      background: linear-gradient(135deg, rgba(0, 176, 255, 0.25), rgba(31, 226, 212, 0.2));
      box-shadow: 0 0 24px rgba(0, 176, 255, 0.25);
      transform: translateY(-1px);

      .v-icon {
        transform: scale(1.1);
      }
    }

    &.router-link-active,
    &.router-link-exact-active {
      background: linear-gradient(135deg, rgba(0, 176, 255, 0.2), rgba(31, 226, 212, 0.15));
      box-shadow: 0 0 20px rgba(0, 176, 255, 0.2);
    }
  }

  /* Right content sticks to the far right */
  .header-right {
    justify-self: end;
    gap: 12px;
  }

  @media (prefers-reduced-motion: reduce) {
    .nav-app-bar,
    .logo-link,
    .logo-glow,
    .nav-trigger,
    .nav-trigger .v-icon,
    .dashboard-cta,
    .dashboard-cta .v-icon {
      transition: none;
    }

    .dashboard-cta:hover {
      transform: none;
    }
  }
</style>

<style lang="scss">
.product-menu {
  background: rgba(15, 23, 48, 0.95) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(120, 163, 255, 0.15);
  padding: 6px;
  min-width: 200px;
}

.product-menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  color: rgba(200, 218, 255, 0.75);
  font-size: 0.9rem;
  font-weight: 500;
  text-decoration: none;
  border-radius: 8px;
  transition: all 0.15s ease;

  .v-icon {
    opacity: 0.5;
    transition: all 0.15s ease;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.06);
    color: rgba(255, 255, 255, 0.95);

    .v-icon {
      opacity: 0.9;
      color: #00B0FF;
    }
  }

  &.router-link-active {
    color: #00B0FF;

    .v-icon {
      opacity: 1;
      color: #00B0FF;
    }
  }
}

.v-theme--lightTheme {
  .product-menu {
    background: rgba(255, 255, 255, 0.97) !important;
    border-color: rgba(28, 110, 227, 0.12);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  }

  .product-menu-item {
    color: rgba(15, 23, 42, 0.7);

    &:hover {
      background: rgba(0, 0, 0, 0.04);
      color: rgba(15, 23, 42, 0.95);

      .v-icon {
        color: var(--token-color-primary, #1c6ee3);
      }
    }

    &.router-link-active {
      color: var(--token-color-primary, #1c6ee3);

      .v-icon {
        color: var(--token-color-primary, #1c6ee3);
      }
    }
  }
}
</style>
